import { Component, NgZone, OnInit, inject } from '@angular/core';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule, DatePipe } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Observable } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MainLayoutComponent } from '../../../_layouts/main-layout/main-layout.component';
import { AlertService } from '../../../_services/alert.service';
import { ContainerReceipt, DeliveryValidationService } from '../../../_services/delivery-validation.service';
import { PurchaseShipmentService } from '../../../_services/purchase-shipment.service';
import { shipmentStatusClass } from '../../purchases/shipments/shipment-status';
import { SHIPMENTS_LIST_KEY, lastListQuery } from '../../purchases/list-state';
import { ContainerCard, ContainerReceiptCardComponent, toContainerCard } from './container-receipt-card.component';
import { findState } from '../../purchases/shipments/container-command';
import { Listening, listenOnce, speechSupported } from '../../../../Shared/_services/speech';

type Source = 'receipts' | 'shipments';

// One container on its own page, in any state. Opened from Delivery
// validation > Incoming Stocks (the yard checks it in here once it's
// Delivered) or from a shipment on the Shipments list (read-only there - the
// check-in belongs to the delivery team).
@Component({
    selector: 'app-container-receipt-detail',
    imports: [CommonModule, DatePipe, RouterLink, MatIconModule, MatTooltipModule, MatProgressSpinnerModule, MatMenuModule, MainLayoutComponent, ContainerReceiptCardComponent],
    templateUrl: './container-receipt-detail.component.html',
    styles: [
        `
      .ctr-mic.is-listening::before,
      .ctr-mic.is-listening::after {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 9999px;
        background: rgba(239, 68, 68, 0.35);
        animation: ctr-pulse 1.6s ease-out infinite;
      }
      .ctr-mic.is-listening::after { animation-delay: 0.8s; }
      @keyframes ctr-pulse {
        from { transform: scale(1); opacity: 0.8; }
        to { transform: scale(2); opacity: 0; }
      }
    `,
    ]
})
export class ContainerReceiptDetailComponent implements OnInit {
  card: ContainerCard | null = null;
  loading = true;
  notFound = false;
  readonly source: Source;
  readonly getStatusClass = shipmentStatusClass;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private deliveryValidationService: DeliveryValidationService,
    private shipmentService: PurchaseShipmentService,
    private alertService: AlertService,
  ) {
    this.source = (this.route.snapshot.data['source'] as Source) ?? 'receipts';
  }

  // ---- Changing the state here (from Shipments: purchasing access only) ----
  private zone = inject(NgZone);
  listening = false;
  saving = false;
  private listener: Listening | null = null;
  readonly stateOptions = [
    { value: 'BOOKED', label: 'Booked' },
    { value: 'IN TRANSIT', label: 'In transit' },
    { value: 'ARRIVED', label: 'Arrived' },
    { value: 'CLEARED', label: 'Cleared' },
    { value: 'DELIVERED', label: 'Delivered' },
  ];

  get canChangeState(): boolean {
    return this.source === 'shipments' && !!this.card && !['RECEIVED', 'CANCELLED'].includes(this.status);
  }

  // Click the mic and say the new state.
  speakState(): void {
    if (this.listening) {
      this.listener?.stop();
      return;
    }
    if (!speechSupported()) {
      this.alertService.alert("This browser can't listen (Brave blocks it) - pick the state from the menu", 'error');
      return;
    }
    this.listening = true;
    this.listener = listenOnce(this.zone);
    this.listener.result
      .then((heard) => {
        this.listening = false;
        const state = findState(heard);
        if (!state) return this.alertService.alert(`Heard “${heard.split(' | ')[0]}” - say a state, e.g. cleared, delivered`, 'error');
        if (state.value === 'RECEIVED') return this.alertService.alert('Received is set by checking it in at Incoming Stocks', 'error');
        if (state.value === this.status) return this.alertService.alert(`Already ${state.label.toLowerCase()}`, 'success');
        this.setState(state.value);
      })
      .catch((message: string) => {
        this.listening = false;
        this.alertService.alert(message, 'error');
      });
  }

  setState(value: string): void {
    if (!this.card) return;
    const label = this.stateOptions.find((o) => o.value === value)?.label ?? value;
    this.saving = true;
    this.shipmentService.setContainerStatus([this.card.id], value).subscribe({
      next: () => {
        this.saving = false;
        if (this.card) {
          this.card.status = value;
          if (['CLEARED', 'DELIVERED'].includes(value) && !this.card.clearedAt) this.card.clearedAt = new Date().toISOString().slice(0, 10);
          if (!['CLEARED', 'DELIVERED'].includes(value)) this.card.clearedAt = null;
        }
        this.alertService.alert(`${this.card?.containerNo || 'Container'} set to ${label}`, 'success');
      },
      error: (error) => {
        this.saving = false;
        this.alertService.alert(error?.error?.message || 'Could not update the container', 'error');
      },
    });
  }

  ngOnInit(): void {
    const id = Number(this.route.snapshot.paramMap.get('id'));
    // Each side reads it through its own access (delivery vs purchasing).
    const request: Observable<ContainerReceipt> =
      this.source === 'shipments' ? this.shipmentService.getContainer(id) : this.deliveryValidationService.getContainerForReceipt(id);
    request.subscribe({
      next: (container) => {
        this.card = toContainerCard(container);
        this.loading = false;
      },
      error: (error) => {
        this.loading = false;
        this.notFound = true;
        if (error?.status !== 404) this.alertService.alert(error?.error?.message || 'Failed to load the container', 'error');
      },
    });
  }

  get status(): string {
    return this.card?.status ?? 'DELIVERED';
  }

  // The check-in form: only for a delivered container, on the delivery side.
  get canCheckIn(): boolean {
    return this.status === 'DELIVERED' && this.source === 'receipts';
  }

  get units(): number {
    return this.card?.lines.reduce((n, l) => n + l.expected, 0) ?? 0;
  }

  get returnedUnits(): number {
    return this.card?.returns?.reduce((n, r) => n + r.quantity, 0) ?? 0;
  }

  get orders(): { reference: string; supplierName: string | null; purchaseOrderId: number }[] {
    const seen = new Map<string, { reference: string; supplierName: string | null; purchaseOrderId: number }>();
    this.card?.lines.forEach((l) => seen.set(l.poReference, { reference: l.poReference, supplierName: l.supplierName, purchaseOrderId: l.purchaseOrderId }));
    return [...seen.values()];
  }

  // One line under the state badge saying where the container is.
  get stateNote(): string {
    const c = this.card;
    if (!c) return '';
    switch (this.status) {
      case 'BOOKED': return c.etd ? `Booked - departs ${new Date(c.etd).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : 'Booked, not yet sailed';
      case 'IN TRANSIT': return c.eta ? `At sea - due ${new Date(c.eta).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}` : 'At sea';
      case 'ARRIVED': return 'At port, waiting for customs clearance';
      case 'CLEARED': return 'Cleared customs - on its way to the yard';
      case 'DELIVERED': return this.source === 'receipts' ? 'At the yard - count it and check it in below' : 'At the yard, waiting for the delivery team to check it in';
      case 'RECEIVED': return 'Checked in at the yard - its stock is booked';
      case 'CANCELLED': return 'This shipment is cancelled';
      default: return '';
    }
  }

  back(): void {
    if (this.source === 'shipments') this.router.navigate(['/shipments'], { queryParams: lastListQuery(SHIPMENTS_LIST_KEY) });
    else this.router.navigate(['/delivery-validation'], { queryParams: { tab: 'RECEIPTS' } });
  }
}
